import {
  SupplierComplianceBaseline,
  SupplierComplianceDelta,
  SupplierComplianceDocument,
  SupplierComplianceFileOrigin,
  SupplierComplianceFileVerdict,
  SupplierComplianceStage,
  SupplierComplianceStandard,
  SupplierComplianceStatus,
  SupplierDocumentCheck,
  SupplierDocumentDecision,
  SupplierDocumentVerdict,
  SupplierSavePlan,
  SupplierStagedFile,
  TenantProcessStateSupplierCompliance,
} from 'models/copilot/Processes/SupplierCompliance/tenantProcessStateSupplierCompliance';
import {
  SupplierIncidentFinding,
  SupplierIncidentsStage,
  TenantProcessStateSupplierIncidents,
} from 'models/copilot/Processes/SupplierIncidents/tenantProcessStateSupplierIncidents';
import { CopilotConversation } from 'models/copilot/copilotConversation';
import { TenantProcess, TenantProcessStatus } from 'models/copilot/tenantProcessStateBase';
import {
  SupplierComplianceBaselineDTO,
  SupplierComplianceStandardDTO,
  SupplierDocumentCheckDTO,
  SupplierStagedFileDTO,
  TenantProcessStateSupplierComplianceDTO,
} from 'models/dto/copilot/tenantProcessStateSupplierComplianceDTO';
import {
  SupplierIncidentFindingDTO,
  TenantProcessStateSupplierIncidentsDTO,
} from 'models/dto/copilot/tenantProcessStateSupplierIncidentsDTO';
import { TenantProcessStateBaseDTO } from 'models/dto/copilot/tenantProcessStateBaseDTO';
import { EntityTypes } from 'models/entity';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import { fromApiDateTimeOptional } from 'utils/datetime';
import { mapFromCopilotConversation, mapFromPromptResponseCitations, mapToCopilotConversation } from './copilotMapping';
import { mapFromTasksArray } from './taskMapping';

// The two Supplier investigations (PLAN-073): their process rows, keyed on (Asset, assetId) per user, and their requests.

//
// Incidents
//
const mapFromSupplierIncidentFinding = (dto: SupplierIncidentFindingDTO): SupplierIncidentFinding => {
  const output = new SupplierIncidentFinding();
  output.date = fromApiDateTimeOptional(dto.date);
  output.kind = dto.kind ?? '';
  output.title = dto.title ?? '';
  output.summary = dto.summary ?? '';
  output.citations = mapFromPromptResponseCitations(dto.citations);
  output.selected = dto.selected === true;
  output.alreadyRegisteredTaskId = dto.alreadyRegisteredTaskId ?? undefined;

  return output;
};

export function mapFromTenantProcessStateSupplierIncidents(
  item: TenantProcessStateBaseDTO,
  cache: GlobalDataCache,
): TenantProcessStateSupplierIncidents {
  const data = JSON.parse(item.data || '{}') as TenantProcessStateSupplierIncidentsDTO;
  const output = new TenantProcessStateSupplierIncidents();
  output.status = item.status;
  output.assetId = data.assetId ?? item.entityId;
  output.assetName = data.assetName ?? '';
  output.stage = (data.stage ?? SupplierIncidentsStage.Investigating) as SupplierIncidentsStage;
  output.findings = (data.findings ?? []).map(mapFromSupplierIncidentFinding);
  output.summaryMarkdown = data.summaryMarkdown ?? undefined;
  output.eligibleTemplateIds = data.eligibleTemplateIds ?? [];
  output.defaultTemplateId = data.defaultTemplateId ?? undefined;
  output.allowedTagIds = data.allowedTagIds ?? [];
  output.incidentTagId = data.incidentTagId ?? undefined;
  output.ownerId = data.ownerId ?? undefined;
  output.newTasks = mapFromTasksArray(data.newTasks, cache);
  output.createdTaskIds = data.createdTaskIds ?? [];
  output.conversation = data.conversation ? mapFromCopilotConversation(data.conversation) : new CopilotConversation();

  return output;
}

// The request: the conversation (with the answered card), the asset, a start's assessment task and the stage command
export function mapToTenantProcessStateSupplierIncidents(
  conversation: CopilotConversation,
  status: TenantProcessStatus,
  assetId: number,
  consumerTaskId: number | undefined,
  command: string | undefined,
): TenantProcessStateBaseDTO {
  const data = new TenantProcessStateSupplierIncidentsDTO();
  data.conversation = mapToCopilotConversation(conversation);
  data.assetId = assetId;
  data.consumerTaskId = consumerTaskId;
  data.command = command;

  return mapToSupplierRequest(TenantProcess.SupplierIncidents, status, assetId, JSON.stringify(data));
}

//
// Compliance
//
const mapFromSupplierComplianceStandard = (dto: SupplierComplianceStandardDTO): SupplierComplianceStandard => {
  const output = new SupplierComplianceStandard();
  output.code = dto.code ?? '';
  output.status = dto.status ?? '';
  output.issuer = dto.issuer ?? undefined;
  output.validFrom = fromApiDateTimeOptional(dto.validFrom);
  output.validTo = fromApiDateTimeOptional(dto.validTo);
  output.citations = mapFromPromptResponseCitations(dto.citations);
  output.fileUrl = dto.fileUrl ?? undefined;
  output.delta = (dto.delta ?? SupplierComplianceDelta.New) as SupplierComplianceDelta;

  return output;
};

const mapFromSupplierComplianceBaseline = (dto: SupplierComplianceBaselineDTO): SupplierComplianceBaseline => {
  const output = new SupplierComplianceBaseline();
  output.investigated = fromApiDateTimeOptional(dto.investigated);
  output.standards = (dto.standards ?? []).map(mapFromSupplierComplianceStandard);
  output.savedLinkIds = dto.savedLinkIds ?? [];
  output.status = (dto.status ?? SupplierComplianceStatus.NotFound) as SupplierComplianceStatus;

  return output;
};

const mapFromSupplierDocumentCheck = (dto: SupplierDocumentCheckDTO): SupplierDocumentCheck => {
  const output = new SupplierDocumentCheck();
  output.verdict = (dto.verdict ?? SupplierDocumentVerdict.Unreadable) as SupplierDocumentVerdict;
  output.issuer = dto.issuer ?? undefined;
  output.standard = dto.standard ?? undefined;
  output.scope = dto.scope ?? undefined;
  output.subject = dto.subject ?? undefined;
  output.validFrom = fromApiDateTimeOptional(dto.validFrom);
  output.validTo = fromApiDateTimeOptional(dto.validTo);
  output.reasons = dto.reasons ?? [];

  return output;
};

const mapFromSupplierStagedFile = (dto: SupplierStagedFileDTO): SupplierStagedFile => {
  const output = new SupplierStagedFile();
  output.index = dto.index ?? 0;
  output.blockId = dto.blockId ?? '';
  output.tempBlobName = dto.tempBlobName ?? '';
  output.fileName = dto.fileName ?? '';
  output.contentType = dto.contentType ?? '';
  output.size = dto.size ?? 0;
  output.sha256 = dto.sha256 ?? '';
  output.sourceUrl = dto.sourceUrl ?? '';
  output.standardCode = dto.standardCode ?? '';
  output.check = dto.check ? mapFromSupplierDocumentCheck(dto.check) : undefined;
  output.decision = (dto.decision ?? SupplierDocumentDecision.Pending) as SupplierDocumentDecision;
  output.origin = (dto.origin ?? SupplierComplianceFileOrigin.Downloaded) as SupplierComplianceFileOrigin;
  output.fileVerdict =
    dto.fileVerdict === null || dto.fileVerdict === undefined
      ? undefined
      : (dto.fileVerdict as SupplierComplianceFileVerdict);
  output.savedLinkId = dto.savedLinkId ?? undefined;
  output.savedVersion = dto.savedVersion ?? undefined;
  output.savedOn = fromApiDateTimeOptional(dto.savedOn ?? undefined);
  if (dto.savePlan) {
    const plan = new SupplierSavePlan();
    plan.folderPath = dto.savePlan.folderPath ?? '';
    plan.fileName = dto.savePlan.fileName ?? '';
    plan.existingLinkId = dto.savePlan.existingLinkId ?? undefined;
    plan.existingVersion = dto.savePlan.existingVersion ?? undefined;
    plan.warnings = dto.savePlan.warnings ?? [];
    output.savePlan = plan;
  }
  output.saveError = dto.saveError ?? undefined;

  return output;
};

export function mapFromTenantProcessStateSupplierCompliance(
  item: TenantProcessStateBaseDTO,
): TenantProcessStateSupplierCompliance {
  const data = JSON.parse(item.data || '{}') as TenantProcessStateSupplierComplianceDTO;
  const output = new TenantProcessStateSupplierCompliance();
  output.status = item.status;
  output.assetId = data.assetId ?? item.entityId;
  output.assetName = data.assetName ?? '';
  output.stage = (data.stage ?? SupplierComplianceStage.Investigating) as SupplierComplianceStage;
  output.previousOutcome = data.previousOutcome ? mapFromSupplierComplianceBaseline(data.previousOutcome) : undefined;
  output.existingDocuments = (data.existingDocuments ?? []).map((dto) => {
    const document = new SupplierComplianceDocument();
    document.linkId = dto.linkId ?? 0;
    document.name = dto.name ?? '';
    document.version = dto.version ?? undefined;
    document.modified = fromApiDateTimeOptional(dto.modified);

    return document;
  });
  output.standards = (data.standards ?? []).map(mapFromSupplierComplianceStandard);
  output.summaryMarkdown = data.summaryMarkdown ?? undefined;
  output.stagedFiles = (data.stagedFiles ?? []).map(mapFromSupplierStagedFile);
  output.consumerTaskId = data.consumerTaskId ?? undefined;
  output.sessionId = data.sessionId ?? undefined;
  output.conversation = data.conversation ? mapFromCopilotConversation(data.conversation) : new CopilotConversation();

  return output;
}

// The request: the conversation, the asset, a start's assessment task and the stage command
export function mapToTenantProcessStateSupplierCompliance(
  conversation: CopilotConversation,
  status: TenantProcessStatus,
  assetId: number,
  consumerTaskId: number | undefined,
  command: string | undefined,
): TenantProcessStateBaseDTO {
  const data = new TenantProcessStateSupplierComplianceDTO();
  data.conversation = mapToCopilotConversation(conversation);
  data.assetId = assetId;
  data.consumerTaskId = consumerTaskId;
  data.command = command;

  return mapToSupplierRequest(TenantProcess.SupplierCompliance, status, assetId, JSON.stringify(data));
}

// both processes key their row on the asset
const mapToSupplierRequest = (
  processId: TenantProcess,
  status: TenantProcessStatus,
  assetId: number,
  data: string,
): TenantProcessStateBaseDTO => {
  const output = new TenantProcessStateBaseDTO();
  output.processId = processId;
  output.typeOfEntity = EntityTypes.Asset;
  output.entityId = assetId;
  output.status = status;
  output.data = data;

  return output;
};
